import { Popover, PopoverAnchor } from "@ui-kit/Popover";
import {
	createContext,
	type ReactNode,
	type RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

type AnchoredPopoverRenderProps = {
	close: () => void;
	closeMenu: () => void;
	contentRef: RefObject<HTMLDivElement>;
};

export type AnchoredPopoverRequest = {
	anchor: HTMLElement;
	render: (props: AnchoredPopoverRenderProps) => ReactNode;
	onClose: () => void;
};

type DropdownMenuAnchoredPopoverContextValue = {
	openPopover: (request: AnchoredPopoverRequest) => void;
	containsTarget: (target: EventTarget | null) => boolean;
};

const DropdownMenuAnchoredPopoverContext = createContext<DropdownMenuAnchoredPopoverContextValue | null>(null);

export const useDropdownMenuAnchoredPopoverContext = () => useContext(DropdownMenuAnchoredPopoverContext);

export const useDropdownMenuAnchoredPopover = () => {
	const context = useContext(DropdownMenuAnchoredPopoverContext);
	if (!context) throw new Error("useDropdownMenuAnchoredPopover must be used inside DropdownMenu");
	return context.openPopover;
};

type DropdownMenuAnchoredPopoverProviderProps = {
	isMenuOpen: boolean;
	onCloseMenu: () => void;
	children: ReactNode;
};

export const DropdownMenuAnchoredPopoverProvider = ({
	isMenuOpen,
	onCloseMenu,
	children,
}: DropdownMenuAnchoredPopoverProviderProps) => {
	const anchorRef = useRef<HTMLElement | null>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const requestRef = useRef<AnchoredPopoverRequest | null>(null);
	const onCloseMenuRef = useRef(onCloseMenu);
	onCloseMenuRef.current = onCloseMenu;
	const [request, setRequest] = useState<AnchoredPopoverRequest | null>(null);

	const close = useCallback(() => {
		requestRef.current?.onClose();
		requestRef.current = null;
		setRequest(null);
	}, []);

	const closeMenu = useCallback(() => onCloseMenuRef.current(), []);

	const openPopover = useCallback((next: AnchoredPopoverRequest) => {
		requestRef.current?.onClose();
		requestRef.current = next;
		anchorRef.current = next.anchor;
		setRequest(next);
	}, []);

	const containsTarget = useCallback(
		(target: EventTarget | null) => target instanceof Node && !!contentRef.current?.contains(target),
		[],
	);

	useEffect(() => {
		if (!isMenuOpen && request) close();
	}, [isMenuOpen, request, close]);

	useEffect(() => () => requestRef.current?.onClose(), []);

	const value = useMemo(() => ({ openPopover, containsTarget }), [openPopover, containsTarget]);

	return (
		<DropdownMenuAnchoredPopoverContext.Provider value={value}>
			{children}
			<Popover onOpenChange={(open) => !open && close()} open={request !== null}>
				<PopoverAnchor virtualRef={anchorRef} />
				{request?.render({ close, closeMenu, contentRef })}
			</Popover>
		</DropdownMenuAnchoredPopoverContext.Provider>
	);
};
